<template>
  <div>
    <page-content-toolbar
      :projectName="`DASHBOARD`"
    >
      <v-btn
        v-if="isUserParameterizer"
        @click="openAddDialog"
        class="float-right caption font-weight-medium mr-4"
        outlined
        tile
        color="primary"
      >
        {{ $t('project.create') }} <v-icon>mdi-arrow-right</v-icon>
      </v-btn>
    </page-content-toolbar>

    <div class="ma-8">
      
      <v-chip
        v-if="searchProjectsQuery"
        class="ma-2"
        close
        @click:close="searchProjectsQuery = ''"
      >
        {{ this.searchProjectsQuery }}
      </v-chip>


      <v-data-table
        v-if="!loadingProjects && projects.length"
        :headers="headers"
        :items="projects"
        :options.sync="options"
        :server-items-length="totalProjects"
        :loading="loading"
        :footer-props="footerProps"
        class="elevation-1 rai-projects-table"
        @click:row="goToProject($event)"
      >
        <template v-slot:item.members="{ item }">
          <div>
            <template v-if="item.members.length">
              <app-avatar
                v-for="member in item.members"
                :key="member.email"
                :user-name="`${member.firstName} ${member.lastName}`"
                :user-email="member.email"
              />
            </template>
            <span v-else>{{ $t('project.noMemberAssigned') }}</span>
          </div>
        </template>
        <template v-slot:item.lastModifiedDate="{ item }">
          {{ new Date(item.lastModifiedDate).toLocaleString($i18n.locale) }}
        </template>
        <template v-slot:item.actions="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-if="isUserParameterizer"
                @click.stop="openEditdialog(item)"
                class="float-right"
                icon
                color="ternary"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon>mdi-pencil-outline</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('project.edit') }}</span>
          </v-tooltip>
        </template>
      </v-data-table>
      <v-progress-circular
        v-else-if="loadingProjects"
        size="200"
        width="15"
        class="d-block mx-auto"
        indeterminate
        color="primary"
      />
      <div v-else>
        {{ $t('project.notFound') }}
      </div>
    </div>

    <add-project-dialog
      :open="openDialog"
      :validate-function="dialogFunction"
      :prop-project="project"
      :edit="editDialog"
      @cancel-edition="openDialog = false"
    />
  </div>
</template>

<script>
import PageContentToolbar from '@/components/layout/PageContentToolbar';
import AddProjectDialog from '@/components/dashboard/AddProjectDialog';
import AppAvatar from '@/components/global/AppAvatar';
import projectService from '@/services/projects.js';
import userRightMixin from '@/mixins/user-right-mixin.js';

export default {
  name: 'Dashboard',

  components: { AddProjectDialog, PageContentToolbar, AppAvatar, },

  mixins: [ userRightMixin ],

  data () {
    return {
      projects: [],
      totalProjects: 0,
      loadingProjects: false,
      loading: false,
      options: {},
      firstLoad: true,
      openDialog: false,
      dialogFunction: () => {},
      editDialog: false,
      project: undefined,
      searchProjectsQuery: '',
    };
  },

  computed: {
    headers () {
      return [{ text: this.$i18n.t('project.projectName'), value: 'name' },
        { text: this.$i18n.t('project.members'), value: 'members', sortable: false },
        { text: this.$i18n.t('global.lastModifiedDate'), value: 'lastModifiedDate' },
        { text: '', value: 'actions', width: '104px', sortable: false },
      ];
    },
    footerProps () {
      return {
        'items-per-page-options': [10, 20, 50, 100],
        'items-per-page-text': this.$i18n.t('global.rowPerPage'),
        'page-text': '{0}-{1} / {2}',
      };
    },
  },

  methods: {
    goToProject (item) {
      this.$router.push({ name: 'projectSteps', params: { id: item.id} });
    },

    openAddDialog () {
      this.dialogFunction = this.addProject;
      this.editDialog = false;
      this.project = undefined;
      this.openDialog = true;
    },

    openEditdialog (project) {
      this.dialogFunction = this.editProject;
      this.editDialog = true;
      this.project = project;
      this.openDialog = true;
    },

    addProject (project) {
      return new Promise((resolve, reject) => {
        projectService.createProject(project).then(res => {
          console.log('create :', res);
          this.projects.unshift(res.data);
          this.openDialog = false;
          this.$emit('raison-event', { type: 'success', messages: [this.$t('project.created')] });
          resolve();
        }).catch(err => {
          console.log(err);
          this.openDialog = false;
          this.$emit('raison-event', { type: 'error', messages: [this.$t('project.createError')] });
          reject();
        });
      });
    },

    editProject (project) {
      return new Promise((resolve, reject) => {
        projectService.updateProject(project).then(res => {
          if (!res.data.id) {
            throw 'Api error';
          }
          const projIndex = this.projects.findIndex(pro => project.id === pro.id);
          this.projects.splice(projIndex, 1, res.data);
          this.openDialog = false;
          this.$emit('raison-event', { type: 'success', messages: [this.$t('project.edited')] });
          resolve();
        }).catch(err => {
          this.openDialog = false;
          this.$emit('raison-event', { type: 'error', messages: [this.$t('project.editError'), err] });
          reject();
        });
      });
    },

    getProjects () {
      this.loading = true;
      const { sortBy, sortDesc, page, itemsPerPage } = this.options
      projectService.getProjects(sortBy, sortDesc, page ? page - 1 : undefined, itemsPerPage, this.searchProjectsQuery).then(res => {
        this.projects = res.data.content;
        this.totalProjects = res.data.page.totalElements;
      }).catch(err => {
        this.$emit('raison-event', { type: 'error', messages:[err] });
      }).finally(() => {
        this.loading = false;
        if (this.loadingProjects) {
          this.loadingProjects = false;
        }
      })
    },
  },

  created () {
    this.loadingProjects = true;
    this.searchProjectsQuery = this.$route.params.query;
    this.getProjects();
  },

  watch: {
    searchProjectsQuery() {
      this.getProjects();
    },
    options: {
      handler () {
        if (this.firstLoad) {
          this.firstLoad = false;
        } else {
          this.getProjects();
        }
      },
      deep: true,
    },
  },
};
</script>

<style>
.droppable {
  border: dashed 2px grey;
  width: 100%;
  height: 40px;
  text-align: center;
}

.rai-projects-table.v-data-table tr {
  cursor: pointer;
}
</style>
